import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CompanyProfileCreateRequest, type DocType, type DocumentNumberFormatView } from '@bits/shared/zod';
import { assertValidNumberTemplate, fyStartYear, renderDocNumber, templateHasFyToken } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const companyProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/company/profile',
  component: CompanyProfilePage,
});

type FormValues = {
  legalName: string;
  gstin?: string;
  pan?: string;
  udyamNo?: string;
  cin?: string;
  registeredAddress: string;
  invoiceBankDetails: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    ifsc: string;
    branch?: string;
  };
  validFrom: string;
};

function CompanyProfilePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  const changeLogo = useMutation({
    mutationFn: async ({ profileId, file }: { profileId: string; file: File }) => {
      const { id } = await api.uploadCompanyLogo(file);
      return api.setCompanyLogo(profileId, { logoAttachmentId: id });
    },
    onSuccess: async () => {
      setLogoError(null);
      await queryClient.invalidateQueries({ queryKey: ['company'] });
    },
    onError: (err) => setLogoError(toMessage(err)),
  });

  const currentQuery = useQuery({
    queryKey: ['company', 'current'],
    queryFn: () => api.getCurrentCompanyProfile().catch(() => null),
    retry: false,
  });
  const allQuery = useQuery({
    queryKey: ['company', 'all'],
    queryFn: () => api.listCompanyProfiles().catch(() => []),
    retry: false,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(CompanyProfileCreateRequest),
    defaultValues: {
      legalName: '',
      registeredAddress: '',
      invoiceBankDetails: { bankName: '', accountName: '', accountNumber: '', ifsc: '' },
      validFrom: new Date().toISOString().slice(0, 10),
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: FormValues) => {
      const logoAttachmentId = logoFile ? (await api.uploadCompanyLogo(logoFile)).id : undefined;
      return api.createCompanyProfile(logoAttachmentId ? { ...body, logoAttachmentId } : body);
    },
    onSuccess: async () => {
      setDialogOpen(false);
      form.reset();
      setLogoFile(null);
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['company'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const onSubmit = (values: FormValues): void => {
    setErrorMessage(null);
    createMutation.mutate(values);
  };

  const current = currentQuery.data ?? null;
  const all = allQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Company profile
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Effective-dated identity used by billing and vault. New versions supersede — no edits in place.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add new version</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New company profile version</DialogTitle>
              <DialogDescription>
                A new row is appended with the effective-from date. Previous versions stay intact.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Legal name" error={form.formState.errors.legalName?.message}>
                  <Input {...form.register('legalName')} />
                </Field>
                <Field label="Valid from (YYYY-MM-DD)" error={form.formState.errors.validFrom?.message}>
                  <Input type="date" {...form.register('validFrom')} />
                </Field>
                <Field label="GSTIN" error={form.formState.errors.gstin?.message}>
                  <Input {...form.register('gstin')} />
                </Field>
                <Field label="PAN" error={form.formState.errors.pan?.message}>
                  <Input {...form.register('pan')} />
                </Field>
                <Field label="Udyam No." error={form.formState.errors.udyamNo?.message}>
                  <Input {...form.register('udyamNo')} />
                </Field>
                <Field label="CIN" error={form.formState.errors.cin?.message}>
                  <Input {...form.register('cin')} />
                </Field>
              </div>
              <Field label="Registered address" error={form.formState.errors.registeredAddress?.message}>
                <Textarea rows={3} {...form.register('registeredAddress')} />
              </Field>
              <Field label="Logo for invoices (PNG or JPEG, up to 2 MB)">
                <Input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setLogoFile(e.currentTarget.files?.[0] ?? null)}
                />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Bank name" error={form.formState.errors.invoiceBankDetails?.bankName?.message}>
                  <Input {...form.register('invoiceBankDetails.bankName')} />
                </Field>
                <Field
                  label="Account name"
                  error={form.formState.errors.invoiceBankDetails?.accountName?.message}
                >
                  <Input {...form.register('invoiceBankDetails.accountName')} />
                </Field>
                <Field
                  label="Account number"
                  error={form.formState.errors.invoiceBankDetails?.accountNumber?.message}
                >
                  <Input {...form.register('invoiceBankDetails.accountNumber')} />
                </Field>
                <Field label="IFSC" error={form.formState.errors.invoiceBankDetails?.ifsc?.message}>
                  <Input {...form.register('invoiceBankDetails.ifsc')} />
                </Field>
                <Field label="Branch" error={form.formState.errors.invoiceBankDetails?.branch?.message}>
                  <Input {...form.register('invoiceBankDetails.branch')} />
                </Field>
              </div>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving...' : 'Save version'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current profile</CardTitle>
          <CardDescription>Whichever version is effective for today.</CardDescription>
        </CardHeader>
        <CardContent>
          {current ? (
            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-3 text-sm">
              <Row label="Logo">
                <div className="flex flex-wrap items-center gap-3">
                  {current.logoAttachmentId ? (
                    <img
                      src={api.companyLogoUrl(current.logoAttachmentId)}
                      alt=""
                      className="h-12 w-auto max-w-[200px] object-contain"
                    />
                  ) : (
                    <span className="text-[hsl(var(--muted-foreground))]">None yet. It prints on every invoice.</span>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0];
                      e.currentTarget.value = '';
                      if (file) changeLogo.mutate({ profileId: current.id, file });
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={changeLogo.isPending}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {changeLogo.isPending ? 'Uploading…' : current.logoAttachmentId ? 'Change logo' : 'Add logo'}
                  </Button>
                  {logoError ? <span className="text-xs text-[hsl(var(--destructive))]">{logoError}</span> : null}
                </div>
              </Row>
              <Row label="Legal name">{current.legalName}</Row>
              <Row label="Valid from">{current.validFrom}</Row>
              <Row label="GSTIN">{current.gstin ?? '—'}</Row>
              <Row label="PAN">{current.pan ?? '—'}</Row>
              <Row label="Udyam">{current.udyamNo ?? '—'}</Row>
              <Row label="CIN">{current.cin ?? '—'}</Row>
              <Row label="Address">
                <span className="whitespace-pre-line">{current.registeredAddress}</span>
              </Row>
              <Row label="Bank">
                {current.invoiceBankDetails.bankName} · {current.invoiceBankDetails.accountNumber} ·{' '}
                {current.invoiceBankDetails.ifsc}
              </Row>
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No profile yet. Add one to enable billing.
            </p>
          )}
        </CardContent>
      </Card>

      <DocumentNumberingCard />

      <Card>
        <CardHeader>
          <CardTitle>All versions</CardTitle>
          <CardDescription>Newest first. Append-only history.</CardDescription>
        </CardHeader>
        <CardContent>
          {all.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {all.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <div className="font-medium">{p.legalName}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      From {p.validFrom} · GSTIN {p.gstin ?? '—'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-[hsl(var(--destructive))]">{error}</p> : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <>
      <div className="text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="break-words text-[hsl(var(--foreground))]">{children}</div>
    </>
  );
}

const DOC_TYPE_LABEL: Record<DocType, string> = {
  tax_invoice: 'Tax invoice',
  bill_of_supply: 'Bill of supply',
  credit_note: 'Credit note',
  receipt: 'Receipt',
};

const TEMPLATE_TOKENS: ReadonlyArray<{ token: string; meaning: string }> = [
  { token: '{SEQ:5}', meaning: 'sequence padded to 5 digits (any width 1–8, or {SEQ} unpadded)' },
  { token: '{FY}', meaning: '26-27' },
  { token: '{FY4}', meaning: '2026-27' },
  { token: '{FYSTART}', meaning: '2026' },
  { token: '{FYEND}', meaning: '2027' },
];

function todayIst(): string {
  return new Date(Date.now() + (5 * 60 + 30) * 60 * 1000).toISOString().slice(0, 10);
}

function DocumentNumberingCard(): JSX.Element {
  const queryClient = useQueryClient();
  const formatsQuery = useQuery({
    queryKey: ['document-number-formats'],
    queryFn: () => api.listDocumentNumberFormats(),
  });
  const [editing, setEditing] = React.useState(false);
  const [docType, setDocType] = React.useState<DocType>('tax_invoice');
  const [template, setTemplate] = React.useState('INV/{FY}/{SEQ:5}');
  const [validFrom, setValidFrom] = React.useState(todayIst());
  const [startAt, setStartAt] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const formats: DocumentNumberFormatView[] = formatsQuery.data ?? [];
  const current = formats.filter((f) => f.isCurrent);
  const history = formats.filter((f) => f.id !== null);

  const startAtNumber = startAt.trim() === '' ? null : Number(startAt);
  const startAtValid =
    startAtNumber === null || (Number.isInteger(startAtNumber) && startAtNumber >= 1 && startAtNumber <= 99_999_999);

  const preview = React.useMemo(() => {
    try {
      assertValidNumberTemplate(template.trim());
      if (!startAtValid) {
        return { ok: false as const, value: 'The starting number must be a whole number from 1 to 99,999,999.' };
      }
      return {
        ok: true as const,
        value: renderDocNumber(template.trim(), fyStartYear(validFrom || todayIst()), startAtNumber ?? 1),
        continuous: !templateHasFyToken(template.trim()),
      };
    } catch (err) {
      return { ok: false as const, value: err instanceof Error ? err.message : 'This format is not valid.' };
    }
  }, [template, validFrom, startAtNumber, startAtValid]);

  const add = useMutation({
    mutationFn: () =>
      api.addDocumentNumberFormat({
        docType,
        template: template.trim(),
        validFrom,
        ...(startAtNumber === null ? {} : { startAt: startAtNumber }),
      }),
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['document-number-formats'] });
      await queryClient.invalidateQueries({ queryKey: ['next-document-number'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const startEditing = (f: DocumentNumberFormatView): void => {
    setDocType(f.docType);
    setTemplate(f.template);
    setValidFrom(todayIst());
    setStartAt('');
    setError(null);
    setEditing(true);
  };

  const canSubmit = preview.ok && /^\d{4}-\d{2}-\d{2}$/.test(validFrom) && !add.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document numbering</CardTitle>
        <CardDescription>
          How invoice, bill of supply, credit note and receipt numbers look. The sequence is
          gapless; it restarts every April when the format includes the year and runs on
          continuously when it does not. A new format applies to documents issued on or after
          its start date; numbers already issued never change.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {formatsQuery.isPending ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {current.map((f) => (
              <div
                key={f.docType}
                className="flex flex-col gap-1 rounded-lg border border-[hsl(var(--border))] px-3 py-2.5 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{DOC_TYPE_LABEL[f.docType]}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => startEditing(f)}>
                    Change
                  </Button>
                </div>
                <code className="font-mono text-base font-semibold">{f.example}</code>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  {f.template}
                  {f.validFrom ? ` · from ${f.validFrom}` : ' · built-in default'}
                </span>
              </div>
            ))}
          </div>
        )}

        {editing ? (
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--border))] p-3">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dnf-type">Document type</Label>
                <Select
                  id="dnf-type"
                  value={docType}
                  onChange={(e) => setDocType(e.currentTarget.value as DocType)}
                >
                  {(Object.keys(DOC_TYPE_LABEL) as DocType[]).map((t) => (
                    <option key={t} value={t}>
                      {DOC_TYPE_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dnf-template">Format</Label>
                <Input
                  id="dnf-template"
                  value={template}
                  className="font-mono"
                  onChange={(e) => setTemplate(e.currentTarget.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dnf-start">Start sequence at</Label>
                <Input
                  id="dnf-start"
                  type="number"
                  min={1}
                  placeholder="1"
                  value={startAt}
                  onChange={(e) => setStartAt(e.currentTarget.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dnf-from">Applies from</Label>
                <Input
                  id="dnf-from"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.currentTarget.value)}
                />
              </div>
            </div>
            <p className="text-sm">
              {preview.ok ? (
                <>
                  <span className="text-[hsl(var(--muted-foreground))]">
                    First number under this format:{' '}
                  </span>
                  <code className="font-mono font-semibold">{preview.value}</code>
                  <span className="text-[hsl(var(--muted-foreground))]">
                    {preview.continuous
                      ? ' · no year in the format, so the sequence continues across financial years'
                      : ' · restarts every April'}
                  </span>
                </>
              ) : (
                <span className="text-[hsl(var(--destructive))]">{preview.value}</span>
              )}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
              {TEMPLATE_TOKENS.map((t) => (
                <span key={t.token}>
                  <code className="font-mono text-[hsl(var(--foreground))]">{t.token}</code>{' '}
                  {t.meaning}
                </span>
              ))}
            </div>
            {error ? (
              <Alert variant="destructive">
                <AlertTitle>Could not save the format</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={!canSubmit} onClick={() => add.mutate()}>
                {add.isPending ? 'Saving…' : 'Save format'}
              </Button>
            </div>
          </div>
        ) : null}

        {history.length > 0 ? (
          <div className="flex flex-col gap-1">
            <div className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              History
            </div>
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {history.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <span className="w-28 text-[hsl(var(--muted-foreground))]">
                    {DOC_TYPE_LABEL[f.docType]}
                  </span>
                  <code className="font-mono">{f.template}</code>
                  {f.startAt !== null ? (
                    <span className="text-[hsl(var(--muted-foreground))]">starts at {f.startAt}</span>
                  ) : null}
                  <span className="text-[hsl(var(--muted-foreground))]">from {f.validFrom}</span>
                  {f.isCurrent ? <Badge variant="default">Current</Badge> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
